import { ComponentType, Suspense, useMemo } from 'react';
import { Route, RouteProps } from 'react-router-dom';
import { withAuthenticationRequired } from '@auth0/auth0-react';

import { ErrorBoundary } from './components/ErrorBoundary';
import { Loading } from './components/Loading';

interface PrivateRouteProps extends RouteProps {
  component: ComponentType<object>;
}

export function PrivateRoute({ component, ...args }: PrivateRouteProps) {
  const Wrapped = useMemo(
    () =>
      withAuthenticationRequired(component, {
        onRedirecting: () => (
          <div className="text-center">
            <Loading text="Redirecting..." />
          </div>
        ),
      }),
    [component],
  );

  return (
    <Route
      {...args}
      render={() => (
        // Per-route ErrorBoundary. The outer boundary in App.tsx still
        // catches anything escaping a route (e.g. a context provider
        // crash), but this one keeps a single broken page from blanking
        // the whole app shell — Header and NotificationPanel live above
        // the route and stay visible if a route component throws.
        //
        // Sits outside Suspense so it also catches lazy-chunk load
        // failures (network errors when fetching the route bundle).
        //
        // No `key` prop needed: react-router 5's Switch unmounts the
        // previously-matching Route on navigation, so this boundary's
        // state resets naturally when the user moves to another route.
        <ErrorBoundary>
          <Suspense fallback={<Loading />}>
            <Wrapped />
          </Suspense>
        </ErrorBoundary>
      )}
    />
  );
}
